import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Check, Edit, Plus, Trash2, X } from "lucide-react";
import { getRequest, postRequest, updateRequest } from "../../services";
import toast from "react-hot-toast";

const AutomationModal = ({
  currentSubjects,
  onClose,
  section,
  currentClass,
}) => {
  const { control, handleSubmit, setValue, watch, register } = useForm({
    defaultValues: {
      teachers: currentSubjects.map((subject) => ({
        mainTeacher: "",
        backupTeacher: "",
        subject: subject.name,
        subjectId: subject.subjectId,
        lecturesPerWeek: "",
        lectureDuration: "",
      })),
      constraints: {
        sessionsAndBreakTimes: [
          {
            day: "monday",
            sessionStart: "",
            sessionEnd: "",
            breaks: [{ start: "", end: "" }],
          },
          {
            day: "tuesday",
            sessionStart: "",
            sessionEnd: "",
            breaks: [{ start: "", end: "" }],
          },
          {
            day: "wednesday",
            sessionStart: "",
            sessionEnd: "",
            breaks: [{ start: "", end: "" }],
          },
          {
            day: "thursday",
            sessionStart: "",
            sessionEnd: "",
            breaks: [{ start: "", end: "" }],
          },
          {
            day: "friday",
            sessionStart: "",
            sessionEnd: "",
            breaks: [{ start: "", end: "" }],
          },
        ],
        specialEvents: [
          {
            name: "",
            date: new Date().toISOString(),
            startTime: "",
            endTime: "",
            description: "",
          },
        ],
      },
      settings: {
        timetableFormat: "weekly",
        startDate: new Date().toISOString(),
        numberOfWeeks: 1,
        language: "english",
        totalWeeklyHours: 0,
      },
    },
  });

  const { fields: teacherFields } = useFieldArray({
    control,
    name: "teachers",
  });

  const {
    fields: specialEventFields,
    append: appendSpecialEvent,
    remove: removeSpecialEvent,
  } = useFieldArray({
    control,
    name: "constraints.specialEvents",
  });

  const [scheduleResponse, setScheduleResponse] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [editedSchedule, setEditedSchedule] = useState(null);
  const [teachersListBySubject, setTeachersListBySubject] = useState({});
  const [mainSearchTerms, setMainSearchTerms] = useState([]);
  const [backupSearchTerms, setBackupSearchTerms] = useState([]);
  const [showMainDropdowns, setShowMainDropdowns] = useState([]);
  const [showBackupDropdowns, setShowBackupDropdowns] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (currentSubjects && currentSubjects.length > 0) {
      setMainSearchTerms(new Array(currentSubjects.length).fill(""));
      setBackupSearchTerms(new Array(currentSubjects.length).fill(""));
      setShowMainDropdowns(new Array(currentSubjects.length).fill(false));
      setShowBackupDropdowns(new Array(currentSubjects.length).fill(false));
    }
  }, [currentSubjects]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowMainDropdowns((prev) => prev.map(() => false));
        setShowBackupDropdowns((prev) => prev.map(() => false));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTeachersForSubjects = async () => {
      try {
        const subjectsToFetch = currentSubjects.filter(
          (subject) => subject.subjectId && !teachersListBySubject[subject.subjectId]
        );

        if (subjectsToFetch.length === 0) return;
        const fetchPromises = subjectsToFetch.map(async (subject) => {
          const response = await getRequest(
            `/api/ClassAssignment/GetAssignedTeachers?subjectId=${subject.subjectId}`
          );
          return {
            subjectId: subject.subjectId,
            teachers:
              response.data?.data?.map((teacher) => ({
                id: teacher.id,
                name: teacher.name,
              })) || [],
          };
        });

        const results = await Promise.all(fetchPromises);
        setTeachersListBySubject((prev) => ({
          ...prev,
          ...results.reduce((acc, { subjectId, teachers }) => {
            acc[subjectId] = teachers;
            return acc;
          }, {}),
        }));
      } catch (error) {
        console.error("Error fetching assigned teachers:", error);
        toast.error("Failed to fetch teachers for subjects.");
      }
    };

    fetchTeachersForSubjects();
  }, [currentSubjects]);

  const calculateTotalWeeklyHours = (sessions) => {
    let totalMinutes = 0;

    sessions.forEach(({ sessionStart, sessionEnd }) => {
      if (sessionStart && sessionEnd) {
        const start = new Date(`2000-01-01T${sessionStart}`);
        const end = new Date(`2000-01-01T${sessionEnd}`);
        const diffMinutes = (end - start) / (1000 * 60);
        if (diffMinutes > 0) {
          totalMinutes += diffMinutes;
        }
      }
    });

    return Math.floor(totalMinutes / 60);
  };

  useEffect(() => {
    // Watch specific fields for all days to trigger recalculation
    const subscription = watch((value, { name }) => {
      if (name && name.includes("constraints.sessionsAndBreakTimes")) {
        const sessions = value.constraints.sessionsAndBreakTimes;
        const totalHours = calculateTotalWeeklyHours(sessions);
        setValue("settings.totalWeeklyHours", totalHours);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const getAvailableBackupTeachers = (currentIndex) => {
    const teachers = watch("teachers");
    const subjectId = teachers[currentIndex].subjectId;
    const teachersList = teachersListBySubject[subjectId] || [];
    return teachersList.filter(
      (teacher) => teacher.name !== teachers[currentIndex].mainTeacher
    );
  };

  const getFilteredMainTeachers = (searchTerm, index) => {
    const teachers = watch("teachers");
    const subjectId = teachers[index].subjectId;
    const teachersList = teachersListBySubject[subjectId] || [];
    return teachersList.filter((teacher) =>
      teacher.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getFilteredBackupTeachers = (searchTerm, index) => {
    return getAvailableBackupTeachers(index).filter((teacher) =>
      teacher.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const formatTimeOnly = (timeString) => {
    if (!timeString) return "00:00:00";
    return timeString.length === 5 ? `${timeString}:00` : timeString;
  };

  const onSubmit = async (data) => {
    const transformedData = {
      Teachers: data.teachers.map((teacher) => ({
        mainTeacher: teacher.mainTeacher,
        backupTeacher: teacher.backupTeacher,
        subject: teacher.subject,
        subjectId: teacher.subjectId,
        lecturesPerWeek: parseInt(teacher.lecturesPerWeek) || 0,
        lectureDuration: parseInt(teacher.lectureDuration) || 0,
      })),
      Constraints: {
        sessionsAndBreakTimes: data.constraints.sessionsAndBreakTimes.map(
          (session) => ({
            day: session.day,
            sessionStart: formatTimeOnly(session.sessionStart),
            sessionEnd: formatTimeOnly(session.sessionEnd),
            breaks: session.breaks.map((breakTime) => ({
              start: formatTimeOnly(breakTime.start),
              end: formatTimeOnly(breakTime.end),
            })),
          })
        ),
        specialEvents: data.constraints.specialEvents.map((event) => ({
          name: event.name,
          date: event.date,
          startTime: formatTimeOnly(event.startTime),
          endTime: formatTimeOnly(event.endTime),
          description: event.description || "",
        })),
      },
      Settings: {
        timetableFormat: data.settings.timetableFormat,
        startDate: data.settings.startDate,
        numberOfWeeks: parseInt(data.settings.numberOfWeeks) || 1,
        language: data.settings.language,
        totalWeeklyHours: data.settings.totalWeeklyHours,
      },
    };

    setIsGenerating(true);

    try {
      const response = await postRequest(
        "/api/Class/GenerateAutomation/generate-automation",
        transformedData
      );

      const scheduleData = JSON.parse(response.data.data);
      setScheduleResponse(scheduleData);
      setEditedSchedule(scheduleData);
      toast.success("Schedule generated successfully!");
    } catch (error) {
      console.error("Error during API call:", error);
      const errorMessage =
        error.response?.data?.title ||
        error.message ||
        "Error generating schedule";
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleScheduleUpdate = (day, index, field, value) => {
    const updatedSchedule = { ...editedSchedule };
    updatedSchedule[day][index][field] = value;
    if (field === "Subject") {
      updatedSchedule[day][index]["MainTeacher"] = "";
    }
    setEditedSchedule(updatedSchedule);
  };

  const removeTimeSlot = (day, index) => {
    const updatedSchedule = { ...editedSchedule };
    updatedSchedule[day].splice(index, 1);
    setEditedSchedule(updatedSchedule);
  };

  const saveEditedSchedule = async () => {
    try {
      const subjectMap = {};
      currentSubjects.forEach((subject) => {
        subjectMap[subject.name] = subject.subjectId;
      });

      const missingSubjects = Object.entries(editedSchedule).flatMap(
        ([day, slots]) => slots.filter((slot) => !subjectMap[slot.Subject])
      );
      if (missingSubjects.length > 0) {
        throw new Error(
          `Missing subject IDs for: ${missingSubjects
            .map((s) => s.Subject)
            .join(", ")}`
        );
      }

      const groupedUpdates = {};
      Object.entries(editedSchedule).forEach(([day, slots]) => {
        slots.forEach((slot) => {
          const subjectId = subjectMap[slot.Subject];
          const [startTime, endTime] = slot.TimeSlot.split(" - ");

          if (!groupedUpdates[subjectId]) {
            groupedUpdates[subjectId] = {
              subjectId: subjectId,
              days: [],
            };
          }

          groupedUpdates[subjectId].days.push({
            day: day.toLowerCase(),
            section,
            startTime: convertToTimeSpan(startTime),
            endTime: convertToTimeSpan(endTime),
          });
        });
      });

      const updatePayload = Object.values(groupedUpdates);

      await updateRequest("/api/Class/UpdateClassSchedules", updatePayload);
      setEditingSchedule(false);
      toast.success("Schedule updated successfully!");
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to update schedule");
    }
  };

  const convertToTimeSpan = (timeStr) => {
    const [time, period] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:00`;
  };

  const handleCopyMondaySchedule = (e) => {
    if (e.target.checked) {
      const mondayData = watch("constraints.sessionsAndBreakTimes.0");
      if (mondayData.sessionStart && mondayData.sessionEnd) {
        const updatedSessions = watch("constraints.sessionsAndBreakTimes").map(
          (day, index) => {
            // Copy Monday's schedule to Tuesday, Wednesday, Thursday (indices 1, 2, 3), but not Friday (index 4)
            if (index === 0 || index === 4) return day;
            return {
              ...day,
              sessionStart: mondayData.sessionStart,
              sessionEnd: mondayData.sessionEnd,
              breaks: mondayData.breaks.map((breakTime) => ({
                start: breakTime.start,
                end: breakTime.end,
              })),
            };
          }
        );

        updatedSessions.forEach((session, index) => {
          // Update only Tuesday, Wednesday, Thursday
          if (index === 0 || index === 4) return;
          setValue(
            `constraints.sessionsAndBreakTimes.${index}.sessionStart`,
            session.sessionStart
          );
          setValue(
            `constraints.sessionsAndBreakTimes.${index}.sessionEnd`,
            session.sessionEnd
          );
          session.breaks.forEach((breakTime, breakIndex) => {
            setValue(
              `constraints.sessionsAndBreakTimes.${index}.breaks.${breakIndex}.start`,
              breakTime.start
            );
            setValue(
              `constraints.sessionsAndBreakTimes.${index}.breaks.${breakIndex}.end`,
              breakTime.end
            );
          });
        });

        // Total weekly hours will be updated via useEffect
      } else {
        toast.error("Please fill Monday's schedule first");
      }
    }
  };

  const getTeachersForSubject = (subjectName) => {
    const subject = currentSubjects.find((s) => s.name === subjectName);
    return subject ? teachersListBySubject[subject.subjectId] || [] : [];
  };

  const renderSchedule = () => {
    if (!editedSchedule) return null;

    return (
      <div className="mt-6 border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Generated Schedule</h3>
          {!editingSchedule ? (
            <button
              type="button"
              className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
              onClick={() => setEditingSchedule(true)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit Schedule
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                type="button"
                className="flex items-center bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                onClick={saveEditedSchedule}
              >
                <Check className="h-4 w-4 mr-1" />
                Save Changes
              </button>
              <button
                type="button"
                className="flex items-center bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                onClick={() => {
                  setEditedSchedule(scheduleResponse);
                  setEditingSchedule(false);
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {Object.entries(editedSchedule).map(([day, slots]) => (
            <div key={day} className="border rounded p-3">
              <h4 className="font-medium capitalize mb-2">{day}</h4>
              <div className="space-y-2">
                {slots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-2 bg-gray-50 rounded"
                  >
                    {editingSchedule ? (
                      <>
                        <input
                          type="text"
                          value={slot.TimeSlot}
                          onChange={(e) =>
                            handleScheduleUpdate(
                              day,
                              index,
                              "TimeSlot",
                              e.target.value
                            )
                          }
                          className="p-1 border rounded w-48"
                        />
                        <select
                          value={slot.Subject}
                          onChange={(e) =>
                            handleScheduleUpdate(
                              day,
                              index,
                              "Subject",
                              e.target.value
                            )
                          }
                          className="p-1 border rounded flex-1"
                        >
                          <option value="">Select Subject</option>
                          {currentSubjects.map((subject) => (
                            <option key={subject.subjectId} value={subject.name}>
                              {subject.name}
                            </option>
                          ))}
                        </select>
                        <select
                          value={slot.MainTeacher}
                          onChange={(e) =>
                            handleScheduleUpdate(
                              day,
                              index,
                              "MainTeacher",
                              e.target.value
                            )
                          }
                          className="p-1 border rounded flex-1"
                          disabled={!slot.Subject}
                        >
                          <option value="">Select Teacher</option>
                          {getTeachersForSubject(slot.Subject).map((teacher) => (
                            <option key={teacher.id} value={teacher.name}>
                              {teacher.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeTimeSlot(day, index)}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="w-48 font-medium">
                          {slot.TimeSlot}
                        </span>
                        <span className="flex-1">{slot.Subject}</span>
                        <span className="flex-1">{slot.MainTeacher}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">
            Constraints and Preferences
          </h3>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Sessions and Break Times</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="copyMondaySchedule"
                  onChange={handleCopyMondaySchedule}
                  className="rounded border-gray-300"
                />
                <label htmlFor="copyMondaySchedule" className="text-sm">
                  Copy Monday's schedule to Tuesday-Thursday
                </label>
              </div>
            </div>
            {watch("constraints.sessionsAndBreakTimes").map(
              (daySession, dayIndex) => (
                <div key={dayIndex} className="mb-4 border-b pb-4">
                  <h5 className="capitalize font-medium mb-2">
                    {daySession.day}
                  </h5>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-1/2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Session Start
                      </label>
                      <Controller
                        name={`constraints.sessionsAndBreakTimes.${dayIndex}.sessionStart`}
                        control={control}
                        render={({ field }) => (
                          <input
                            type="time"
                            className="p-2 border rounded w-full"
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Session End
                      </label>
                      <Controller
                        name={`constraints.sessionsAndBreakTimes.${dayIndex}.sessionEnd`}
                        control={control}
                        render={({ field }) => (
                          <input
                            type="time"
                            className="p-2 border rounded w-full"
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="ml-4">
                    <h6 className="text-sm font-medium text-gray-700 mb-2">
                      Break Times
                    </h6>
                    {daySession.breaks.map((_, breakIndex) => (
                      <div
                        key={breakIndex}
                        className="flex items-center gap-4 mb-2"
                      >
                        <Controller
                          name={`constraints.sessionsAndBreakTimes.${dayIndex}.breaks.${breakIndex}.start`}
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className="p-2 border rounded w-40"
                              placeholder="Break start"
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                            />
                          )}
                        />
                        <span>to</span>
                        <Controller
                          name={`constraints.sessionsAndBreakTimes.${dayIndex}.breaks.${breakIndex}.end`}
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className="p-2 border rounded w-40"
                              placeholder="Break end"
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                            />
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Add Special Events</h4>
              <button
                type="button"
                className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                onClick={() =>
                  appendSpecialEvent({
                    name: "",
                    date: new Date().toISOString().split("T")[0],
                    startTime: "",
                    endTime: "",
                    description: "",
                  })
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Special Event
              </button>
            </div>

            {specialEventFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-4 gap-3 mb-3">
                <Controller
                  name={`constraints.specialEvents.${index}.name`}
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Event Name"
                      className="p-2 border rounded"
                    />
                  )}
                />
                <Controller
                  name={`constraints.specialEvents.${index}.date`}
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className="p-2 border rounded"
                    />
                  )}
                />
                <div className="flex gap-2">
                  <Controller
                    name={`constraints.specialEvents.${index}.startTime`}
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="time"
                        placeholder="From"
                        className="p-2 border rounded w-full"
                      />
                    )}
                  />
                  <Controller
                    name={`constraints.specialEvents.${index}.endTime`}
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="time"
                        placeholder="To"
                        className="p-2 border rounded w-full"
                      />
                    )}
                  />
                </div>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white w-8 h-8 rounded flex items-center justify-center"
                  onClick={() => removeSpecialEvent(index)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Teacher Subject Details</h3>
          </div>

          {teacherFields.map((field, index) => (
            <div key={field.id} className="border rounded p-4 mb-4 shadow-sm">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded bg-gray-50"
                  value={watch(`teachers.${index}.subject`)}
                  disabled
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative dropdown-container">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Main Teacher
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={mainSearchTerms[index] || ""}
                    onChange={(e) => {
                      const newSearchTerms = [...mainSearchTerms];
                      newSearchTerms[index] = e.target.value;
                      setMainSearchTerms(newSearchTerms);
                      const newShowDropdowns = [...showMainDropdowns];
                      newShowDropdowns[index] = true;
                      setShowMainDropdowns(newShowDropdowns);
                    }}
                    onFocus={() => {
                      const newShowDropdowns = [...showMainDropdowns];
                      newShowDropdowns[index] = true;
                      setShowMainDropdowns(newShowDropdowns);
                    }}
                    placeholder="Search for a main teacher..."
                  />
                  {showMainDropdowns[index] &&
                    watch(`teachers.${index}.subjectId`) && (
                      <div className="absolute z-10 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto mt-1">
                        {getFilteredMainTeachers(
                          mainSearchTerms[index] || "",
                          index
                        ).map((t) => (
                          <div
                            key={t.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              if (
                                watch(`teachers.${index}.backupTeacher`) ===
                                t.name
                              ) {
                                setValue(`teachers.${index}.backupTeacher`, "");
                                const newBackupTerms = [...backupSearchTerms];
                                newBackupTerms[index] = "";
                                setBackupSearchTerms(newBackupTerms);
                              }

                              setValue(`teachers.${index}.mainTeacher`, t.name);

                              const newSearchTerms = [...mainSearchTerms];
                              newSearchTerms[index] = t.name;
                              setMainSearchTerms(newSearchTerms);

                              const newShowDropdowns = [...showMainDropdowns];
                              newShowDropdowns[index] = false;
                              setShowMainDropdowns(newShowDropdowns);
                            }}
                          >
                            {t.name}
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div className="relative dropdown-container">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Backup Teacher
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={backupSearchTerms[index] || ""}
                    onChange={(e) => {
                      const newSearchTerms = [...backupSearchTerms];
                      newSearchTerms[index] = e.target.value;
                      setBackupSearchTerms(newSearchTerms);
                      const newShowDropdowns = [...showBackupDropdowns];
                      newShowDropdowns[index] = true;
                      setShowBackupDropdowns(newShowDropdowns);
                    }}
                    onFocus={() => {
                      const newShowDropdowns = [...showBackupDropdowns];
                      newShowDropdowns[index] = true;
                      setShowBackupDropdowns(newShowDropdowns);
                    }}
                    placeholder="Search for a backup teacher..."
                    disabled={!watch(`teachers.${index}.mainTeacher`)}
                  />
                  {showBackupDropdowns[index] &&
                    watch(`teachers.${index}.mainTeacher`) && (
                      <div className="absolute z-10 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto mt-1">
                        {getFilteredBackupTeachers(
                          backupSearchTerms[index] || "",
                          index
                        ).map((t) => (
                          <div
                            key={t.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setValue(
                                `teachers.${index}.backupTeacher`,
                                t.name
                              );

                              const newSearchTerms = [...backupSearchTerms];
                              newSearchTerms[index] = t.name;
                              setBackupSearchTerms(newSearchTerms);

                              const newShowDropdowns = [...showBackupDropdowns];
                              newShowDropdowns[index] = false;
                              setShowBackupDropdowns(newShowDropdowns);
                            }}
                          >
                            {t.name}
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Lectures in a Week
                  </label>
                  <Controller
                    name={`teachers.${index}.lecturesPerWeek`}
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="number"
                        placeholder="Enter number of lectures"
                        className="w-full p-2 border rounded"
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lecture Duration (minutes)
                  </label>
                  <Controller
                    name={`teachers.${index}.lectureDuration`}
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="number"
                        placeholder="Enter duration in minutes"
                        className="w-full p-2 border rounded"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">General Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timetable Format
              </label>
              <Controller
                name="settings.timetableFormat"
                control={control}
                render={({ field }) => (
                  <select {...field} className="w-full p-2 border rounded">
                    <option value="weekly">Weekly</option>
                  </select>
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <Controller
                name="settings.startDate"
                control={control}
                render={({ field }) => (
                  <input
                    type="date"
                    className="w-full p-2 border rounded"
                    value={
                      field.value
                        ? new Date(field.value).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      field.onChange(date.toISOString());
                    }}
                  />
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Weekly Hours
              </label>
              <Controller
                name="settings.totalWeeklyHours"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    className="w-full p-2 border rounded"
                    readOnly
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Schedule"}
          </button>
        </div>
        {renderSchedule()}
      </form>
    </div>
  );
};

export default AutomationModal;